import React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';

/**
 * Brand-aligned dropdown select.
 *
 * Wraps `@radix-ui/react-select` so we get keyboard support, type-to-jump,
 * and proper ARIA semantics for free, but uses the same CSS-variable
 * idiom every other settings card uses (border-primary, bg-primary,
 * primary-700 for accents). Drop-in replacement for native `<select>`
 * across the app — the existing shadcn `ui/select.tsx` exposes every
 * sub-piece individually, which is fine for power users but verbose for
 * "just give me a dropdown" call sites; this is the verbose-less variant.
 *
 * For very long lists (50+) build a separate Combobox with type-ahead
 * filtering — Radix Select handles type-to-jump but doesn't filter the
 * popup down to matches.
 */

export interface BrandSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface Props {
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  options: BrandSelectOption[];
  placeholder?: string;
  /** Render a leading "—" entry that resolves to null. */
  allowEmpty?: boolean;
  emptyLabel?: string;
  disabled?: boolean;
  id?: string;
  /** Width override for the trigger. Popup mirrors trigger width by default. */
  className?: string;
  ariaLabel?: string;
  /** Native tooltip shown on hover over the trigger. */
  title?: string;
}

export const BrandSelect: React.FC<Props> = ({
  value,
  onChange,
  options,
  placeholder,
  allowEmpty = false,
  emptyLabel = '—',
  disabled,
  id,
  className,
  ariaLabel,
  title,
}) => {
  // Radix uses '' as a special "clear" sentinel and disallows option
  // values of ''. Map our null externally to a private sentinel so the
  // empty entry is selectable without warnings.
  const NULL_SENTINEL = '__none__';
  const internal = value == null ? NULL_SENTINEL : value;
  return (
    <SelectPrimitive.Root
      value={internal}
      onValueChange={(v) => onChange(v === NULL_SENTINEL ? null : v)}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        id={id}
        aria-label={ariaLabel}
        title={title}
        className={
          'group inline-flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ' +
          (className ?? '')
        }
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border-primary)',
          color: 'var(--color-text-primary)',
          // @ts-expect-error — CSS custom property
          '--tw-ring-color': 'var(--color-primary-500)',
          minHeight: 44,
        }}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown
            className="w-4 h-4 transition-transform group-data-[state=open]:rotate-180"
            style={{ color: 'var(--color-text-tertiary)' }}
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-[300] overflow-hidden rounded-xl border shadow-lg data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border-primary)',
            minWidth: 'var(--radix-select-trigger-width)',
            maxHeight: 'min(360px, var(--radix-select-content-available-height))',
          }}
        >
          <SelectPrimitive.Viewport className="p-1">
            {allowEmpty && (
              <BrandItem value={NULL_SENTINEL} label={emptyLabel} />
            )}
            {options.map((opt) => (
              <BrandItem
                key={opt.value}
                value={opt.value}
                label={opt.label}
                disabled={opt.disabled}
              />
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
};

const BrandItem: React.FC<{
  value: string;
  label: string;
  disabled?: boolean;
}> = ({ value, label, disabled }) => (
  <SelectPrimitive.Item
    value={value}
    disabled={disabled}
    className="relative flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-[color-mix(in_srgb,var(--color-primary-500)_8%,transparent)] data-[state=checked]:font-semibold data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed"
    style={{ color: 'var(--color-text-primary)' }}
  >
    <SelectPrimitive.ItemText>{label}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator className="ms-auto">
      <Check
        className="w-4 h-4"
        style={{ color: 'var(--color-primary-700)' }}
      />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
);
